/**
 * traitCategories.js
 *
 * Disease category definitions and SNP classification.
 * Checks both ClinVar conditions (snp.conditions) and GWAS traits
 * (snp.gwas.traits) against keyword lists to assign one or more
 * broad disease buckets to a SNP.
 *
 * Design notes:
 * - A SNP can match multiple categories (e.g. rs429358/APOE matches
 *   both Neurological and Cardiovascular).
 * - Matching is substring, case-insensitive.
 * - Categories are ordered by rough clinical salience for display.
 * - 'quantitative' is a pseudo-category for measurement/lab-value
 *   traits that are not disease outcomes — derived from isQuantitativeTrait().
 */

import { isQuantitativeTrait } from './riskInterpreter';

export const DISEASE_CATEGORIES = [
  {
    id: 'cardiovascular',
    label: 'Cardiovascular',
    icon: '❤️',
    color: 'bg-red-50 hover:bg-red-100 text-red-800 border-red-200',
    keywords: [
      'cardiac', 'cardio', 'heart', 'coronary', 'artery', 'arterial',
      'atrial', 'ventricular', 'myocardial', 'infarction', 'stroke',
      'hypertension', 'blood pressure', 'systolic', 'diastolic',
      'atherosclerosis', 'thrombosis', 'thromboembolism', 'embolism',
      'aneurysm', 'aortic', 'vascular', 'peripheral artery',
      'heart failure', 'cardiomyopathy', 'arrhythmia', 'fibrillation',
      'qt interval', 'pr interval', 'pulse rate', 'heart rate',
      'ldl', 'hdl', 'cholesterol', 'lipoprotein', 'triglyceride',
      'lipid', 'dyslipidemia', 'hypercholesterolemia',
    ],
  },
  {
    id: 'neurological',
    label: 'Neurological',
    icon: '🧠',
    color: 'bg-purple-50 hover:bg-purple-100 text-purple-800 border-purple-200',
    keywords: [
      "alzheimer", 'dementia', 'parkinson', 'multiple sclerosis',
      'epilepsy', 'seizure', 'migraine', 'headache',
      'amyotrophic lateral sclerosis', 'als', 'huntington',
      'neuropathy', 'neurodegenerative', 'neurological',
      'cognitive', 'intelligence', 'educational attainment',
      'schizophrenia', 'bipolar', 'depression', 'anxiety',
      'autism', 'adhd', 'attention deficit',
      'brain', 'neuron', 'cortical', 'cerebellar', 'cerebral',
      'psychiatric', 'mental health', 'psychosis',
      'sleep', 'insomnia', 'narcolepsy',
    ],
  },
  {
    id: 'metabolic',
    label: 'Metabolic',
    icon: '⚗️',
    color: 'bg-amber-50 hover:bg-amber-100 text-amber-800 border-amber-200',
    keywords: [
      'diabetes', 'diabetic', 'insulin', 'glucose', 'glycemic',
      'obesity', 'obese', 'body mass', 'bmi', 'adiposity',
      'waist', 'fat mass', 'overweight',
      'metabolic syndrome', 'metabolism',
      'thyroid', 'hypothyroid', 'hyperthyroid', 'tsh',
      'gout', 'uric acid', 'urate',
      'fatty liver', 'nafld', 'nash',
      'phenylketonuria', 'pku',
      'glycogen storage', 'lysosomal storage',
      'wilson', 'hemochromatosis', 'iron overload',
    ],
  },
  {
    id: 'cancer',
    label: 'Cancer',
    icon: '🎗️',
    color: 'bg-rose-50 hover:bg-rose-100 text-rose-800 border-rose-200',
    keywords: [
      'cancer', 'carcinoma', 'tumor', 'tumour', 'malignant', 'malignancy',
      'neoplasm', 'neoplasia', 'adenoma', 'sarcoma', 'lymphoma',
      'leukemia', 'leukaemia', 'melanoma', 'glioma', 'glioblastoma',
      'breast cancer', 'prostate cancer', 'colorectal cancer',
      'lung cancer', 'ovarian cancer', 'cervical cancer',
      'pancreatic cancer', 'gastric cancer', 'bladder cancer',
      'hepatocellular', 'renal cell', 'thyroid cancer',
      'myeloma', 'hodgkin', 'non-hodgkin',
      'metastasis', 'oncology', 'oncogenic',
      'brca', 'tumor suppressor',
    ],
  },
  {
    id: 'immune',
    label: 'Immune & Inflammatory',
    icon: '🛡️',
    color: 'bg-orange-50 hover:bg-orange-100 text-orange-800 border-orange-200',
    keywords: [
      'autoimmune', 'immune', 'immunodeficiency', 'immunoglobulin',
      'rheumatoid arthritis', 'lupus', 'sle',
      'inflammatory bowel', 'crohn', 'ulcerative colitis',
      'celiac', 'coeliac',
      'psoriasis', 'eczema', 'atopic dermatitis',
      'asthma', 'allergy', 'allergic', 'anaphylaxis',
      'multiple sclerosis', 'myasthenia',
      'sjogren', 'scleroderma', 'vasculitis',
      'inflammation', 'inflammatory', 'cytokine',
      'c-reactive protein', 'crp', 'interleukin',
    ],
  },
  {
    id: 'musculoskeletal',
    label: 'Musculoskeletal',
    icon: '🦴',
    color: 'bg-stone-50 hover:bg-stone-100 text-stone-800 border-stone-200',
    keywords: [
      'bone', 'osteoporosis', 'osteoarthritis', 'arthritis',
      'fracture', 'bone mineral density', 'bmd',
      'muscle', 'muscular dystrophy', 'myopathy',
      'scoliosis', 'spinal', 'vertebral', 'disc',
      'joint', 'cartilage', 'tendon', 'ligament',
      'height', 'stature', 'growth',
      'grip strength', 'lean mass',
    ],
  },
  {
    id: 'renal',
    label: 'Renal & Urological',
    icon: '🫘',
    color: 'bg-teal-50 hover:bg-teal-100 text-teal-800 border-teal-200',
    keywords: [
      'kidney', 'renal', 'nephropathy', 'nephritis', 'nephrotic',
      'glomerular', 'glomerulonephritis',
      'urinary', 'bladder', 'urological',
      'creatinine', 'albumin', 'egfr', 'glomerular filtration',
      'kidney disease', 'ckd', 'end-stage renal',
      'polycystic kidney', 'pkd',
    ],
  },
  {
    id: 'gastrointestinal',
    label: 'Gastrointestinal',
    icon: '🫁',
    color: 'bg-lime-50 hover:bg-lime-100 text-lime-800 border-lime-200',
    keywords: [
      'gastrointestinal', 'bowel', 'colon', 'colorectal',
      'gastric', 'stomach', 'gastroesophageal', 'gerd', 'reflux',
      'liver', 'hepatic', 'hepatitis', 'cirrhosis', 'biliary',
      'gallstone', 'gallbladder', 'pancreas', 'pancreatitis',
      'celiac', 'coeliac', 'malabsorption',
      'irritable bowel', 'ibs', 'constipation',
    ],
  },
  {
    id: 'respiratory',
    label: 'Respiratory',
    icon: '🫧',
    color: 'bg-sky-50 hover:bg-sky-100 text-sky-800 border-sky-200',
    keywords: [
      'lung', 'pulmonary', 'respiratory', 'bronchial', 'bronchitis',
      'asthma', 'copd', 'emphysema',
      'forced expiratory', 'fev', 'fvc', 'lung function',
      'pneumonia', 'fibrosis', 'interstitial lung',
      'sleep apnea', 'apnoea',
    ],
  },
  {
    id: 'reproductive',
    label: 'Reproductive & Hormonal',
    icon: '🔬',
    color: 'bg-pink-50 hover:bg-pink-100 text-pink-800 border-pink-200',
    keywords: [
      'reproductive', 'fertility', 'infertility', 'endometriosis',
      'polycystic ovary', 'pcos',
      'testosterone', 'estrogen', 'oestrogen', 'progesterone',
      'menopause', 'menstrual', 'ovarian',
      'prostate', 'testicular',
      'preeclampsia', 'gestational',
      'sex hormone', 'gonadal',
    ],
  },
  {
    id: 'haematological',
    label: 'Blood & Haematological',
    icon: '🩸',
    color: 'bg-red-50 hover:bg-red-100 text-red-900 border-red-300',
    keywords: [
      'blood', 'haematological', 'hematological',
      'haemoglobin', 'hemoglobin', 'anaemia', 'anemia',
      'sickle cell', 'thalassemia', 'thalassaemia',
      'platelet', 'thrombocytopenia', 'thrombocytosis',
      'white blood cell', 'red blood cell', 'leucocyte',
      'lymphocyte', 'neutrophil', 'eosinophil', 'basophil',
      'coagulation', 'clotting', 'factor', 'von willebrand',
      'deep vein thrombosis', 'dvt', 'pulmonary embolism',
    ],
  },
  {
    id: 'quantitative',
    label: 'Quantitative Traits',
    icon: '📐',
    color: 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-300',
    // Determined programmatically via isQuantitativeTrait(), not keywords
    keywords: [],
  },
];

// Fast lookup map
const CATEGORY_MAP = Object.fromEntries(DISEASE_CATEGORIES.map(c => [c.id, c]));
export const getDiseaseCategory = (id) => CATEGORY_MAP[id];

/**
 * Given a free-form string (conditions or trait name), return the IDs of all
 * matching disease categories.
 */
const matchCategories = (text) => {
  if (!text) return [];
  const lower = text.toLowerCase();
  return DISEASE_CATEGORIES
    .filter(cat => cat.id !== 'quantitative' && cat.keywords.some(kw => lower.includes(kw)))
    .map(cat => cat.id);
};

/**
 * Return the set of disease category IDs that apply to a SNP.
 * Checks both snp.conditions (ClinVar) and snp.gwas.traits (GWAS Catalog).
 *
 * The 'quantitative' pseudo-category is applied when ANY GWAS trait on the
 * SNP is a quantitative measurement (using isQuantitativeTrait logic inline
 * to avoid a circular import — same keyword list).
 */
export const classifySnpDiseaseCategories = (snp) => {
  const matched = new Set();

  // ClinVar conditions string (pipe-separated, e.g. "Diabetes|not provided")
  if (snp.conditions) {
    const parts = snp.conditions.split('|');
    parts.forEach(part => matchCategories(part).forEach(id => matched.add(id)));
  }

  // GWAS traits array
  if (snp.gwas?.traits?.length) {
    snp.gwas.traits.forEach(trait => {
      matchCategories(trait).forEach(id => matched.add(id));
    });
  }

  // Quantitative pseudo-category — any GWAS trait that is a measurement
  if (snp.gwas?.topAssociations?.length) {
    const hasQuant = snp.gwas.topAssociations.some(a => isQuantitativeTrait(a.trait));
    if (hasQuant) matched.add('quantitative');
  }

  return [...matched];
};

/**
 * Returns true if a SNP matches the active disease filter.
 * filter is an array of category IDs; empty array or ['all'] means no filter.
 */
export const snpMatchesDiseaseFilter = (snp, filter) => {
  if (!filter || filter.length === 0 || filter.includes('all')) return true;
  const snpCategories = classifySnpDiseaseCategories(snp);
  return filter.some(id => snpCategories.includes(id));
};

/**
 * Count how many SNPs in the result set fall into each disease category.
 * Used by FilterBar to show/grey-out counts.
 */
export const getDiseaseCategoryCounts = (snps) => {
  const counts = Object.fromEntries(DISEASE_CATEGORIES.map(c => [c.id, 0]));
  snps.forEach(snp => {
    classifySnpDiseaseCategories(snp).forEach(id => {
      if (counts[id] !== undefined) counts[id]++;
    });
  });
  return counts;
};